import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { HiMail, HiClock, HiCheck, HiExclamation, HiChevronDown } from 'react-icons/hi';
import { FaWhatsapp } from 'react-icons/fa';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { COMPANY_INFO, FORM_OPTIONS } from '../utils/constants';

const FAQ_ITEMS = [
  { q: 'How long does setup take?', a: 'Most projects go live within 5 working days. Complex integrations may take a few days longer.' },
  { q: 'Do I need any technical knowledge?', a: 'Not at all. We handle everything from design to deployment. You just tell us what you need automated.' },
  { q: 'What does the free audit include?', a: 'A 20-minute call where we map your current workflow, identify bottlenecks, and show you exactly what can be automated and what it would cost.' },
  { q: 'Is there a long-term contract?', a: 'No. Our monthly plans are flexible and can be paused or cancelled anytime. Setup fees are one-time.' },
  { q: 'Can you integrate with my existing tools?', a: 'Yes. We integrate with WhatsApp Business API, CRMs, payment gateways, Google Sheets, and most popular business tools used in India.' },
  { q: 'What happens if something breaks?', a: 'Our monthly support retainer includes monitoring, maintenance, and priority response. We fix issues before they impact your business.' },
];

const ContactSection = () => {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation({ threshold: 0.1 });
  const [openFaq, setOpenFaq] = useState(null);

  const [formData, setFormData] = useState({
    fullName: '', businessName: '', industry: '',
    phone: '+91', description: '', source: ''
  });
  const [formState, setFormState] = useState('idle');
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Required';
    if (!formData.businessName.trim()) newErrors.businessName = 'Required';
    if (!formData.industry) newErrors.industry = 'Required';
    if (!formData.phone.trim() || formData.phone === '+91') newErrors.phone = 'Required';
    if (!formData.description.trim()) newErrors.description = 'Required';
    if (!formData.source) newErrors.source = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setFormState('submitting');
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setFormState('success');
      setTimeout(() => {
        setFormData({ fullName: '', businessName: '', industry: '', phone: '+91', description: '', source: '' });
        setFormState('idle');
      }, 3000);
    } catch {
      setFormState('error');
      setTimeout(() => setFormState('idle'), 5000);
    }
  };

  const inputStyle = (hasError) => ({
    width: '100%',
    padding: '12px 16px',
    backgroundColor: '#FFFFFF',
    border: `1.5px solid ${hasError ? '#dc2626' : 'var(--color-border)'}`,
    borderRadius: '8px',
    color: '#1A1A1A',
    fontSize: '0.95rem',
    fontFamily: '"DM Sans", sans-serif',
    outline: 'none',
    transition: 'border-color 0.15s',
  });

  const labelStyle = {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#1A1A1A',
    marginBottom: '0.5rem',
    fontFamily: '"DM Sans", sans-serif',
  };

  if (formState === 'success') {
    return (
      <section style={{ backgroundColor: '#F5F0E8', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            width: '4rem', height: '4rem', borderRadius: '50%',
            backgroundColor: '#1A1A1A', display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 1.5rem',
          }}>
            <HiCheck size={28} color="#FFFFFF" />
          </div>
          <h2 style={{ fontSize: '2rem', fontWeight: 700, color: '#1A1A1A', marginBottom: '1rem', fontFamily: '"DM Sans", sans-serif' }}>
            Thank you!
          </h2>
          <p style={{ fontSize: '1rem', color: '#666666', fontFamily: '"DM Sans", sans-serif' }}>
            We'll get back to you within 4 business hours.
          </p>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* Contact Form Section */}
      <section
        id="contact"
        ref={sectionRef}
        style={{
          backgroundColor: '#F5F0E8',
          padding: 'clamp(4rem, 8vw, 7rem) 1.5rem',
          borderTop: '1px solid var(--color-border)',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '3rem',
          }} className="contact-grid">
            {/* Left: info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={sectionVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: '#1A1A1A',
                marginBottom: '1rem',
                fontFamily: '"DM Sans", sans-serif',
              }}>
                Let's talk automation
              </h2>
              <p style={{
                fontSize: '1rem',
                lineHeight: 1.65,
                color: '#666666',
                marginBottom: '2rem',
                fontFamily: '"DM Sans", sans-serif',
              }}>
                Tell us about your business and we'll show you exactly what can be automated.
              </p>

              {/* Contact methods */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <a href={COMPANY_INFO.whatsappUrl} target="_blank" rel="noopener noreferrer" style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '1rem', background: '#FFFFFF',
                  border: '1px solid var(--color-border)', borderRadius: '12px',
                  textDecoration: 'none',
                }}>
                  <FaWhatsapp size={20} color="#1A1A1A" />
                  <div>
                    <div style={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.9rem', fontFamily: '"DM Sans", sans-serif' }}>WhatsApp</div>
                    <div style={{ color: '#666666', fontSize: '0.8rem', fontFamily: '"DM Sans", sans-serif' }}>Quick conversations</div>
                  </div>
                </a>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '1rem', background: '#FFFFFF',
                  border: '1px solid var(--color-border)', borderRadius: '12px',
                }}>
                  <HiMail size={20} color="#1A1A1A" />
                  <div>
                    <div style={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.9rem', fontFamily: '"DM Sans", sans-serif' }}>Email</div>
                    <div style={{ color: '#666666', fontSize: '0.8rem', fontFamily: '"DM Sans", sans-serif' }}>{COMPANY_INFO.email}</div>
                  </div>
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '1rem', background: '#FFFFFF',
                  border: '1px solid var(--color-border)', borderRadius: '12px',
                }}>
                  <HiClock size={20} color="#1A1A1A" />
                  <div>
                    <div style={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.9rem', fontFamily: '"DM Sans", sans-serif' }}>Response time</div>
                    <div style={{ color: '#666666', fontSize: '0.8rem', fontFamily: '"DM Sans", sans-serif' }}>{COMPANY_INFO.responseTime}</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={sectionVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
            >
              <form onSubmit={handleSubmit} style={{
                background: '#FFFFFF', border: '1px solid var(--color-border)',
                borderRadius: '16px', padding: 'clamp(1.5rem, 3vw, 2rem)',
              }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1A1A1A', marginBottom: '1.5rem', fontFamily: '"DM Sans", sans-serif' }}>
                  Get started today
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }} className="form-2col">
                  <div>
                    <label style={labelStyle}>Full Name *</label>
                    <input name="fullName" value={formData.fullName} onChange={handleInputChange} placeholder="Your name" style={inputStyle(errors.fullName)} />
                  </div>
                  <div>
                    <label style={labelStyle}>Business Name *</label>
                    <input name="businessName" value={formData.businessName} onChange={handleInputChange} placeholder="Business name" style={inputStyle(errors.businessName)} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }} className="form-2col">
                  <div>
                    <label style={labelStyle}>Industry *</label>
                    <select name="industry" value={formData.industry} onChange={handleInputChange} style={inputStyle(errors.industry)}>
                      <option value="">Select industry</option>
                      {FORM_OPTIONS.industries.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Phone *</label>
                    <input name="phone" type="tel" value={formData.phone} onChange={handleInputChange} style={inputStyle(errors.phone)} />
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle}>What do you want to automate? *</label>
                  <textarea name="description" value={formData.description} onChange={handleInputChange} rows={3} placeholder="Brief description..." style={{ ...inputStyle(errors.description), resize: 'none' }} />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={labelStyle}>How did you find us? *</label>
                  <select name="source" value={formData.source} onChange={handleInputChange} style={inputStyle(errors.source)}>
                    <option value="">Select</option>
                    {FORM_OPTIONS.sources.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <button type="submit" disabled={formState === 'submitting'} style={{
                  width: '100%', padding: '14px',
                  backgroundColor: '#1A1A1A', color: '#FFFFFF',
                  border: 'none', borderRadius: '999px',
                  fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
                  fontFamily: '"DM Sans", sans-serif',
                  opacity: formState === 'submitting' ? 0.6 : 1,
                }}>
                  {formState === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </motion.div>
          </div>
        </div>

        <style>{`
          @media (min-width: 768px) {
            .contact-grid { grid-template-columns: 1fr 1.2fr !important; gap: 4rem !important; }
          }
          @media (max-width: 640px) {
            .form-2col { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </section>

      {/* FAQ Section */}
      <section style={{
        backgroundColor: '#FAF7F2',
        padding: 'clamp(4rem, 8vw, 7rem) 1.5rem',
        borderTop: '1px solid var(--color-border)',
      }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: '#1A1A1A',
            textAlign: 'center',
            marginBottom: '3rem',
            fontFamily: '"DM Sans", sans-serif',
          }}>
            Frequently asked questions
          </h2>

          <div style={{ borderTop: '1px solid var(--color-border)' }}>
            {FAQ_ITEMS.map((faq, idx) => (
              <div key={idx} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <button
                  onClick={() => setOpenFaq(prev => prev === idx ? null : idx)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    padding: '1.25rem 0',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span style={{
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#1A1A1A',
                    fontFamily: '"DM Sans", sans-serif',
                  }}>
                    {faq.q}
                  </span>
                  <HiChevronDown
                    size={20}
                    color="#666666"
                    style={{
                      flexShrink: 0,
                      transition: 'transform 0.2s',
                      transform: openFaq === idx ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                </button>
                {openFaq === idx && (
                  <div style={{ paddingBottom: '1.25rem', paddingRight: '2.5rem' }}>
                    <p style={{
                      fontSize: '0.9rem',
                      lineHeight: 1.7,
                      color: '#666666',
                      fontFamily: '"DM Sans", sans-serif',
                    }}>
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactSection;
